import React, { useContext } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import Downshift from "downshift";
import LpgSearchSolid from "@/icons/Lpg/SearchSolid";

import { useUniqueId } from "../hooks/useUniqueId";
import { Icon } from "../Icon";
import { TextLink } from "../TextLink";
import { CopyText } from "../CopyText";
import { ThemeContext } from "../Theme";

import {
  ReactDefaultProps,
  ReactComponentStaticProps,
} from "../../types/react";

import styles from "./Search.scss";

const variantValues = ["small", "large"] as const;
type VariantValue = typeof variantValues[number];

type SearchBaseProps = {
  placeholder?: string;
  quickLinksLabel?: string;
  links: { label: string; url: string }[];
  items: { value: string; url: string }[];
  variant?: VariantValue;
  customClass?: string;
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  innerRef?: React.Ref<HTMLElement>;
};

export type SearchProps = SearchBaseProps &
  Omit<
    React.ComponentPropsWithoutRef<"input">,
    "children" | keyof SearchBaseProps
  >;

const defaultProps = {
  placeholder: "",
  quickLinksLabel: "",
  onSubmit: (event: React.FormEvent) => {
    event.preventDefault();
    return false;
  },
  innerRef: null,
  customClass: undefined,
  variant: "small",
} as const;

const validatedDefaultProps: ReactDefaultProps<
  SearchBaseProps,
  typeof defaultProps
> = defaultProps;

const propTypes = {
  placeholder: PropTypes.string,
  quickLinksLabel: PropTypes.string,
  links: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }).isRequired
  ).isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }).isRequired
  ).isRequired,
  customClass: PropTypes.string,
  onSubmit: PropTypes.func,
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.instanceOf(
        typeof Element === "undefined"
          ? // eslint-disable-next-line @typescript-eslint/no-empty-function
            (((() => {}) as unknown) as typeof HTMLElement)
          : HTMLElement
      ).isRequired,
    }),
  ]),
  id: PropTypes.string,
  name: PropTypes.string,
  variant: PropTypes.oneOf(variantValues),
} as const;

export const Search: ((props: SearchProps) => JSX.Element) &
  ReactComponentStaticProps<SearchProps> = (props) => {
  const {
    placeholder,
    quickLinksLabel,
    links,
    items,
    customClass,
    onSubmit,
    innerRef,
    id,
    name,
    variant,
    ...restProps
  } = props;
  const context = useContext(ThemeContext);
  const classes = classNames(styles.search, customClass, {
    [context !== undefined ? styles[context] : ""]: context,
    [styles["search--large"]]: variant === "large",
  });
  const searchId = id || `search-${useUniqueId("Search", props) + 1}`;
  const searchName = name || searchId;

  return (
    <Downshift
      id={searchId}
      itemToString={(item) => (item ? item.value : "")}
      environment={typeof window !== "undefined" ? window : undefined}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        isOpen,
        inputValue,
        highlightedIndex,
      }) => (
        <form className={classes} onSubmit={onSubmit}>
          <div className={styles.group}>
            <input
              name={searchName}
              type="text"
              placeholder={placeholder}
              className={styles.input}
              {...getInputProps({ ref: innerRef, ...restProps })}
            />
            <div className={styles.iconSection}>
              <button type="submit" className={styles.icon}>
                <span className={styles.iconSvg}>
                  <Icon icon={LpgSearchSolid} size={Icon.Size.small} />
                </span>
              </button>
            </div>
          </div>
          <ul
            className={isOpen ? styles.listActive : styles.list}
            {...getMenuProps()}
          >
            {isOpen
              ? items
                  .filter(
                    (item) => !inputValue || item.value.includes(inputValue)
                  )
                  .map((item, index) => (
                    <li
                      className={
                        highlightedIndex === index
                          ? styles.itemActive
                          : styles.item
                      }
                      {...getItemProps({ index, item })}
                      key={item.value}
                    >
                      {item.value}
                    </li>
                  ))
              : null}
            <li className={styles.quickLinksSection}>
              <p className={styles.quickLinksLabel}>{quickLinksLabel}</p>
              <ul className={styles.quickLinksList}>
                {isOpen
                  ? links.map((itemLink, index) => (
                      <li
                        key={`${itemLink.label}-${itemLink.url}`}
                        className={styles.quickLinksItem}
                      >
                        <CopyText>
                          <TextLink href={itemLink.url}>
                            {`${index ? ", " : ""} ${itemLink.label}`}
                          </TextLink>
                        </CopyText>
                      </li>
                    ))
                  : null}
              </ul>
            </li>
          </ul>
        </form>
      )}
    </Downshift>
  );
};

Search.propTypes = propTypes;

Search.defaultProps = validatedDefaultProps;

Search.displayName = "Search";

export default Search;
